// /ops/brokers — core broker financial integrity (SM-725, SM-621 phase
// 08d).
//
// Renders the broker-recon sweep's per node × currency snapshot (ops-api
// /admin/ops/aggregates/broker) and adds nothing of its own: every
// figure is one node's, in one currency, as the sweep copied it from the
// ledger / broker-service read it came from. Per-root cards and the
// conservation table show one row per currency — never a combined total
// across currencies (G8).
//
// Filters: root, currency, lifecycle state (URL search, shareable). The
// currency starts at GOLD (SM-870): every broker node trades GOLD only;
// RED and GREEN rows are the SYSTEM_BROKER's, or a balance left from a
// tree that once traded game mode, and stay one pick away. Open
// cases are the ops findings of the broker money task types (broker,
// exposure, clearing), linked into the inbox with the same scope.
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { BROKER_CURRENCIES, BROKER_NODE_CURRENCIES } from "@/components/brokers/governance";
import { CanonAmount, LifecycleBadge, clearingDirectionLabel } from "@/components/ops/brokerRecon";
import { msToIso } from "@/components/brokers/receivables";
import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { CurrencyBadge } from "@/components/ui/CurrencyAmount";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { fmtRelative } from "@/lib/format";
import { fetchBrokerPositions, opsKeys } from "@/lib/ops-queries";
import { BROKER_LIFECYCLE_STATES, type BrokerPosition } from "@/lib/ops-types";

interface Search {
  root?: string;
  currency?: string;
  state?: string;
}

// Codes are identifiers, not copy: the option shows the code.
const CURRENCIES: readonly string[] = BROKER_CURRENCIES;
/** The currency filter's "no filter" value; GOLD is the default. */
const EVERY_CURRENCY = "ALL";
const DEFAULT_CURRENCY = BROKER_NODE_CURRENCIES[0];

export const Route = createFileRoute("/_app/ops/brokers/")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    root: typeof s.root === "string" && s.root.trim() ? s.root.trim() : undefined,
    currency: [...CURRENCIES, EVERY_CURRENCY].find((c) => c !== DEFAULT_CURRENCY && c === s.currency),
    state: BROKER_LIFECYCLE_STATES.find((c) => c === s.state),
  }),
  component: BrokerIntegrityPage,
});

const th = "px-3 py-2.5 text-left text-2xs font-medium uppercase tracking-wide text-muted";
const td = "px-3 py-2 text-xs";

function BrokerIntegrityPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const currency = search.currency ?? DEFAULT_CURRENCY;
  const params = {
    root_broker_id: search.root,
    currency: currency === EVERY_CURRENCY ? undefined : currency,
    lifecycle_state: search.state,
  };
  const q = useQuery({
    queryKey: opsKeys.brokerPositions(params),
    queryFn: () => fetchBrokerPositions(params),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
  // The root filter offers every root in the unfiltered snapshot.
  const all = useQuery({
    queryKey: opsKeys.brokerPositions({}),
    queryFn: () => fetchBrokerPositions({}),
  });
  const roots = rootsOf(all.data?.items ?? []);
  const items = q.data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-56">
          <Select
            value={search.root ?? ""}
            onChange={(e) => navigate({ search: (s) => ({ ...s, root: e.target.value || undefined }) })}
            placeholder="Every root"
            options={roots.map((r) => ({ value: r.id, label: r.name }))}
          />
        </div>
        <div className="w-40">
          <Select
            aria-label="Currency"
            value={currency}
            onChange={(e) =>
              navigate({ search: (s) => ({ ...s, currency: e.target.value === DEFAULT_CURRENCY ? undefined : e.target.value }) })
            }
            options={[...CURRENCIES.map((c) => ({ value: c, label: c })), { value: EVERY_CURRENCY, label: "Every currency" }]}
          />
        </div>
        <div className="w-40">
          <Select
            value={search.state ?? ""}
            onChange={(e) => navigate({ search: (s) => ({ ...s, state: e.target.value || undefined }) })}
            placeholder="Every state"
            options={BROKER_LIFECYCLE_STATES.map((c) => ({ value: c, label: c }))}
          />
        </div>
        <div className="ml-auto text-xs text-muted">
          {q.data && !q.data.empty && q.data.computed_at_unix_ms > 0 ? (
            <span title="When the broker reconciliation last wrote this snapshot">
              {fmtRelative(msToIso(q.data.computed_at_unix_ms) ?? "")}
            </span>
          ) : null}
        </div>
      </div>

      {q.isLoading ? <Skeleton className="h-40" /> : null}
      {q.isError ? <ErrorPanel error={q.error} reset={() => void q.refetch()} /> : null}
      {q.data?.empty ? (
        <EmptyState
          title="No broker reconciliation snapshot yet"
          description="The broker_recon_hourly sweep writes one per node and currency on its first run."
        />
      ) : null}
      {q.data && !q.data.empty && items.length === 0 ? (
        <EmptyState title="No broker matches these filters" description="Clear a filter to see more nodes." />
      ) : null}

      {items.length > 0 ? (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            {rootsOf(items).map((r) => (
              <RootCard key={r.id} rootID={r.id} name={r.name} rows={items.filter((i) => i.root_broker_id === r.id)} />
            ))}
          </div>
          <ConservationTable rows={items} />
          <OpenCases rows={items} root={search.root} currency={params.currency} />
        </>
      ) : null}
    </div>
  );
}

function rootsOf(items: BrokerPosition[]): { id: string; name: string }[] {
  const seen = new Map<string, string>();
  for (const i of items) {
    if (!seen.has(i.root_broker_id)) seen.set(i.root_broker_id, i.root_broker_id);
    if (i.broker_id === i.root_broker_id) seen.set(i.root_broker_id, i.display_name || i.broker_id);
  }
  return [...seen.entries()].map(([id, name]) => ({ id, name }));
}

/** Σ over the nodes of one tree, within ONE currency (never across). */
function sumCanon(rows: BrokerPosition[], pick: (r: BrokerPosition) => string | undefined): string {
  let t = 0n;
  for (const r of rows) {
    const v = pick(r);
    if (v && /^-?\d+$/.test(v)) t += BigInt(v);
  }
  return t.toString();
}

function RootCard({ rootID, name, rows }: { rootID: string; name: string; rows: BrokerPosition[] }) {
  const currencies = [...new Set(rows.map((r) => r.currency))].sort();
  const open = rows.reduce((n, r) => n + r.open_findings, 0);
  const critical = rows.reduce((n, r) => n + r.open_critical, 0);
  const rootRow = (c: string) => rows.find((r) => r.broker_id === rootID && r.currency === c);
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Link to="/ops/brokers/$brokerId" params={{ brokerId: rootID }} className="hover:underline">
            {name}
          </Link>
        </CardTitle>
        <div className="flex items-center gap-2">
          {critical > 0 ? (
            <Badge tone="danger" title="Open critical findings">
              {critical}
            </Badge>
          ) : null}
          <Badge tone={open > 0 ? "warning" : "success"} title="Open findings">
            {open}
          </Badge>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-surface-2">
            <tr>
              <th className={`${th} px-5`}>Currency</th>
              <th className={`${th} text-right`}>Pools</th>
              <th className={`${th} text-right`}>Allocated to users</th>
              <th className={`${th} text-right`}>Revenue</th>
              <th className={`${th} text-right`}>Clearing</th>
              <th className={`${th} text-right`}>Headroom</th>
            </tr>
          </thead>
          <tbody>
            {currencies.map((c) => {
              const rs = rows.filter((r) => r.currency === c);
              const top = rootRow(c);
              const hasClearing = rs.some((r) => r.clearing_balance !== undefined);
              return (
                <tr key={c} className="border-b border-border/60 last:border-0">
                  <td className={`${td} px-5`}>
                    <CurrencyBadge currency={c} />
                  </td>
                  <td className={`${td} text-right`}>
                    <CanonAmount currency={c} value={sumCanon(rs, (r) => r.pool_total)} />
                  </td>
                  <td className={`${td} text-right`}>
                    <CanonAmount currency={c} value={sumCanon(rs, (r) => r.allocated_outstanding)} />
                  </td>
                  <td className={`${td} text-right`}>
                    <CanonAmount
                      currency={c}
                      value={sumCanon(rs, (r) => (BigInt(r.revenue_pending) + BigInt(r.revenue_available)).toString())}
                    />
                  </td>
                  <td className={`${td} text-right`}>
                    {hasClearing ? <CanonAmount currency={c} value={sumCanon(rs, (r) => r.clearing_balance)} tone="auto" /> : "—"}
                  </td>
                  <td className={`${td} text-right`}>
                    {top?.exposure_known && top.has_limit ? <CanonAmount currency={c} value={top.headroom} /> : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardBody>
    </Card>
  );
}

function ConservationTable({ rows }: { rows: BrokerPosition[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Conservation by node and currency</CardTitle>
        <span className="text-2xs text-muted">One row per node and currency; nothing is added across currencies.</span>
      </CardHeader>
      <CardBody className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-surface-2">
              <tr>
                <th className={`${th} px-5`}>Node</th>
                <th className={th}>State</th>
                <th className={th}>Currency</th>
                <th className={`${th} text-right`}>Pool available</th>
                <th className={`${th} text-right`}>Reserved</th>
                <th className={`${th} text-right`}>Pending payout</th>
                <th className={`${th} text-right`}>Allocated to users</th>
                <th className={`${th} text-right`}>Revenue pending</th>
                <th className={`${th} text-right`}>Revenue available</th>
                <th className={`${th} text-right`}>Payout reserved</th>
                <th className={`${th} text-right`}>Clearing</th>
                <th className={`${th} text-right`}>Exposure / limit</th>
                <th className={`${th} text-right`}>Open</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.broker_id}:${r.currency}`} className="border-b border-border/60 last:border-0">
                  <td className={`${td} px-5`}>
                    <span style={{ paddingLeft: `${r.depth * 16}px` }}>
                      <Link to="/ops/brokers/$brokerId" params={{ brokerId: r.broker_id }} className="hover:underline">
                        {r.display_name || r.broker_id}
                      </Link>
                    </span>
                  </td>
                  <td className={td}>
                    <LifecycleBadge state={r.lifecycle_state} />
                  </td>
                  <td className={td}>
                    <CurrencyBadge currency={r.currency} />
                  </td>
                  <td className={`${td} text-right`}>
                    <CanonAmount currency={r.currency} value={r.pool_available} />
                  </td>
                  <td className={`${td} text-right`}>
                    <CanonAmount currency={r.currency} value={r.pool_reserved} />
                  </td>
                  <td className={`${td} text-right`}>
                    <CanonAmount currency={r.currency} value={r.pool_pending} />
                  </td>
                  <td className={`${td} text-right`}>
                    <CanonAmount currency={r.currency} value={r.allocated_outstanding} />
                  </td>
                  <td className={`${td} text-right`}>
                    <CanonAmount currency={r.currency} value={r.revenue_pending} />
                  </td>
                  <td className={`${td} text-right`}>
                    <CanonAmount currency={r.currency} value={r.revenue_available} />
                  </td>
                  <td className={`${td} text-right`}>
                    <CanonAmount currency={r.currency} value={r.payout_reserved} />
                  </td>
                  <td className={`${td} text-right`} title={clearingDirectionLabel(r.clearing_direction)}>
                    {r.clearing_balance !== undefined ? (
                      <CanonAmount currency={r.currency} value={r.clearing_balance} tone="auto" />
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className={`${td} text-right`}>
                    {r.exposure_known ? (
                      <span className="inline-flex items-center gap-1">
                        <CanonAmount currency={r.currency} value={r.exposure} />
                        {r.has_limit ? (
                          <>
                            <span className="text-muted">/</span>
                            <CanonAmount currency={r.currency} value={r.limit} />
                            <span className="text-2xs text-muted" title="Limit used">
                              {pct(r.utilisation_bps)}
                            </span>
                          </>
                        ) : null}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className={`${td} text-right`}>
                    {r.open_findings > 0 ? (
                      <Link
                        to="/ops/findings"
                        search={{ broker_id: r.broker_id, currency: r.currency, only_open: "true" }}
                        className="hover:underline"
                      >
                        <Badge tone={r.open_critical > 0 ? "danger" : "warning"}>{r.open_findings}</Badge>
                      </Link>
                    ) : (
                      <span className="text-muted">0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}

function OpenCases({ rows, root, currency }: { rows: BrokerPosition[]; root?: string; currency?: string }) {
  const byCode = new Map<string, number>();
  for (const r of rows) {
    for (const [code, n] of Object.entries(r.open_findings_by_code)) byCode.set(code, (byCode.get(code) ?? 0) + n);
  }
  const codes = [...byCode.entries()].sort((a, b) => b[1] - a[1]);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Open cases by code</CardTitle>
        <Link to="/ops/findings" search={{ task_type: "broker", only_open: "true", root_broker_id: root, currency }} className="text-2xs text-neon hover:underline">
          Broker findings inbox
        </Link>
      </CardHeader>
      <CardBody>
        {codes.length === 0 ? (
          <p className="text-sm text-muted">No open broker, exposure or clearing finding in this scope.</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {codes.map(([code, n]) => (
              <li key={code}>
                <Link
                  to="/ops/findings"
                  search={{ code, only_open: "true", root_broker_id: root, currency }}
                  className="inline-flex items-center gap-2 rounded border border-border px-2 py-1 font-mono text-2xs hover:bg-surface-2"
                >
                  {code}
                  <Badge tone="warning">{n}</Badge>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function pct(bps: number): string {
  return `${(bps / 100).toFixed(1)}%`;
}
